import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  TrendingUp, 
  Globe, 
  Network, 
  Shield, 
  Target,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  MessageCircle
} from 'lucide-react';
import { services, founders, partners, valuePropositions, stats, companyInfo, transactionProcess, insights, globalNetwork } from '../data/mock';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [assessmentData, setAssessmentData] = useState({
    requirements: [],
    transactionSize: '',
    geography: '',
    timeline: '',
    status: ''
  });

  const iconMap = {
    Building2,
    TrendingUp,
    Globe,
    Network,
    Shield,
    Target
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAssessmentChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setAssessmentData(prev => ({
        ...prev,
        requirements: checked
          ? [...prev.requirements, value]
          : prev.requirements.filter(req => req !== value)
      }));
    } else {
      setAssessmentData({
        ...assessmentData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setShowSuccessMessage(true);
        // Don't reset form yet, keep data for context
      } else {
        toast.error('Error', {
          description: response.data.message,
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error', {
        description: 'Failed to send message. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssessmentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/strategic-assessment`, {
        ...formData,
        ...assessmentData
      });
      
      if (response.data.success) {
        toast.success('Assessment Submitted!', {
          description: 'High-priority mandates are typically reviewed within 24 hours.',
          duration: 6000
        });
        
        // Reset both forms
        setFormData({ name: '', email: '', phone: '' });
        setAssessmentData({
          requirements: [],
          transactionSize: '',
          geography: '',
          timeline: '',
          status: ''
        });
        setShowAssessmentForm(false);
        setShowSuccessMessage(false);
      } else {
        toast.error('Error', {
          description: response.data.message,
        });
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Error', {
        description: 'Failed to submit assessment. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Strategic Transaction Advisory
            </h1>
            <p className="hero-subtitle-large">
              Real Estate • Capital Advisory • Global Trade
            </p>
            <p className="hero-description">
              Facilitating high-value transactions for developers, investors and institutions across India and global markets.
            </p>
            
            {/* Hero Metrics */}
            <div className="hero-metrics">
              <div className="hero-metric-card">
                <div className="hero-metric-label">Real Estate Transaction Pipeline</div>
                <div className="hero-metric-value">$400 Mn+</div>
              </div>
              <div className="hero-metric-card">
                <div className="hero-metric-label">Capital & Financing Pipeline</div>
                <div className="hero-metric-value">$350 Mn+</div>
              </div>
              <div className="hero-metric-card">
                <div className="hero-metric-label">Global Trade Pipeline</div>
                <div className="hero-metric-value">$250 Mn+</div>
              </div>
            </div>
            
            <div className="hero-buttons">
              <a href="#contact" className="btn-primary btn-large">
                Discuss an Opportunity
                <ArrowRight size={20} />
              </a>
              <a href="/active-mandates" className="btn-secondary">
                View Active Mandates
              </a>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop" 
              alt="Strategic Advisory"
              className="hero-img"
            />
          </div>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Value Propositions */}
      <section className="value-section">
        <div className="section-header">
          <h2 className="section-title">Why Leading Institutions Choose HSNL Advisory</h2>
          <p className="section-description">
            Delivering transaction certainty through strategic expertise and institutional relationships
          </p>
        </div>
        <div className="value-grid">
          {valuePropositions.map((value) => {
            const IconComponent = iconMap[value.icon];
            return (
              <div key={value.id} className="value-card">
                <div className="value-icon">
                  {IconComponent && <IconComponent size={32} />}
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-header">
          <h2 className="section-title">Advisory Capabilities</h2>
          <p className="section-description">
            Comprehensive transaction advisory across real estate, investment banking, and international trade
          </p>
        </div>
        
        {/* Service Navigation Cards */}
        <div className="services-nav-grid">
          <Link to="/services/real-estate" className="service-nav-card">
            <div className="service-nav-icon">
              <Building2 size={40} />
            </div>
            <h3 className="service-nav-title">Real Estate Advisory</h3>
            <p className="service-nav-description">
              Strategic land advisory and institutional investments across India
            </p>
            <div className="service-nav-arrow">
              <ArrowRight size={20} />
            </div>
          </Link>

          <Link to="/services/investment-finance" className="service-nav-card">
            <div className="service-nav-icon">
              <TrendingUp size={40} />
            </div>
            <h3 className="service-nav-title">Investment & Finance Advisory</h3>
            <p className="service-nav-description">
              Capital structuring and financing for large-scale projects
            </p>
            <div className="service-nav-arrow">
              <ArrowRight size={20} />
            </div>
          </Link>

          <Link to="/services/international-trade" className="service-nav-card">
            <div className="service-nav-icon">
              <Globe size={40} />
            </div>
            <h3 className="service-nav-title">International Trade</h3>
            <p className="service-nav-description">
              Global trade facilitation across commodities and energy sectors
            </p>
            <div className="service-nav-arrow">
              <ArrowRight size={20} />
            </div>
          </Link>
        </div>
      </section>

      {/* Founders Section */}
      <section id="about" className="founders-section">
        <div className="section-header">
          <h2 className="section-title">Founders</h2>
          <p className="section-description">
            Strategic Transaction Advisors & Deal Facilitators
          </p>
        </div>
        <div className="founders-grid">
          {founders.map((founder) => (
            <div key={founder.id} className="founder-card">
              <div className="founder-image-container">
                <img src={founder.image} alt={founder.name} className="founder-image" />
              </div>
              <div className="founder-content">
                <h3 className="founder-name">{founder.name}</h3>
                <p className="founder-title">{founder.title}</p>
                <p className="founder-education">{founder.education}</p>
                <p className="founder-experience">Previously at: {founder.experience}</p>
                <p className="founder-description">{founder.description}</p>
                <div className="founder-expertise">
                  <h4 className="expertise-heading">Key Expertise:</h4>
                  <ul className="expertise-list">
                    {founder.expertise.map((skill, idx) => (
                      <li key={idx} className="expertise-item">
                        <CheckCircle size={14} />
                        <span>{skill}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners Section */}
      <section className="advisors-section">
        <div className="section-header">
          <p className="section-pre-title">Guided by experienced leaders across healthcare, global trade, and strategic operations</p>
          <h2 className="section-title">Our Partners</h2>
          <p className="section-description">
            Strategic partnerships with industry leaders across healthcare, global trade, and emerging sectors
          </p>
        </div>
        <div className="partners-grid">
          {partners.map((partner) => (
            <div key={partner.id} className="partner-card">
              <div className="partner-image-container">
                <img src={partner.image} alt={partner.name} className="partner-image" />
              </div>
              <div className="partner-content">
                <h4 className="partner-name">{partner.name}</h4>
                <p className="partner-title">{partner.title}</p>
                <p className="partner-bio">{partner.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* Transaction Process - How We Work */}
      <section className="transaction-process-section">
        <div className="section-header">
          <h2 className="section-title">How We Work</h2>
          <p className="section-description">
            Structured transaction advisory process ensuring seamless execution
          </p>
        </div>
        <div className="process-steps">
          {transactionProcess.map((step) => (
            <div key={step.step} className="process-step">
              <div className="process-step-number">Step {step.step}</div>
              <h3 className="process-step-title">{step.title}</h3>
              <p className="process-step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Institutional Partnerships */}
      <section className="institutional-partnerships-section">
        <div className="service-detail-content">
          <h2 className="section-title">Institutional Partnerships</h2>
          <p className="institutional-partnerships-text">
            HSNL Advisory works with developers, family offices, institutional investors and global capital partners across India and international markets.
          </p>
        </div>
      </section>

      {/* UHNI Credibility Sections */}
      <section className="uhni-credibility-section">
        <div className="uhni-grid">
          <div className="uhni-card">
            <h3 className="uhni-card-title">Confidential Mandates</h3>
            <p className="uhni-card-text">
              HSNL Advisory works on select confidential mandates with developers, institutional investors and family offices across India and global markets.
            </p>
          </div>
          <div className="uhni-card">
            <h3 className="uhni-card-title">Typical Mandate Size</h3>
            <div className="uhni-metric">USD 10 Million+</div>
          </div>
          <div className="uhni-card">
            <h3 className="uhni-card-title">Global Capital Network</h3>
            <p className="uhni-card-text">
              Our network includes investors, developers, family offices and capital partners across India, Middle East and global markets.
            </p>
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section className="insights-section">
        <div className="section-header">
          <h2 className="section-title">Insights</h2>
          <p className="section-description">
            Market intelligence and strategic perspectives on real estate and investment opportunities
          </p>
        </div>
        <div className="insights-grid">
          {insights.map((insight) => (
            <div key={insight.id} className="insight-card">
              <div className="insight-category">{insight.category}</div>
              <h3 className="insight-title">{insight.title}</h3>
              <p className="insight-excerpt">{insight.excerpt}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2 className="contact-title">Initiate a Conversation</h2>
            <p className="contact-description">
              Connect with our advisory team to explore strategic opportunities for your investment portfolio.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <Mail size={24} />
                <div>
                  <p className="contact-label">Email</p>
                  <a href={`mailto:${companyInfo.email}`} className="contact-value">
                    {companyInfo.email}
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <Phone size={24} />
                <div>
                  <p className="contact-label">Phone</p>
                  <a href={`tel:${companyInfo.phone}`} className="contact-value">
                    {companyInfo.phone}
                  </a>
                  {companyInfo.phone2 && (
                    <>
                      <br />
                      <a href={`tel:${companyInfo.phone2}`} className="contact-value">
                        {companyInfo.phone2}
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div className="contact-item">
                <MapPin size={24} />
                <div>
                  <p className="contact-label">Location</p>
                  <p className="contact-value">{companyInfo.address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            {!showSuccessMessage && !showAssessmentForm && (
              <>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Mobile Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  
                  <p className="form-disclaimer">
                    Due to the bespoke nature of our services, we work with a limited number of clients. 
                    Submissions are reviewed based on relevance and transaction readiness.
                  </p>
                  
                  <button type="submit" className="btn-primary form-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Processing...' : 'Get Access to Strategic Assessment'}
                    <ArrowRight size={20} />
                  </button>
                </form>
              </>
            )}

            {showSuccessMessage && !showAssessmentForm && (
              <div className="success-message-container">
                <div className="success-icon">
                  <CheckCircle size={48} />
                </div>
                <h3 className="success-title">Thank you. You are one step away from accessing tailored opportunities.</h3>
                <p className="success-text">
                  Kindly complete the next step below for evaluation.
                </p>
                <button 
                  onClick={() => setShowAssessmentForm(true)} 
                  className="btn-primary"
                  style={{marginTop: '1.5rem'}}
                >
                  Proceed to Strategic Assessment
                  <ArrowRight size={20} />
                </button>
                
                <div className="whatsapp-cta" style={{marginTop: '2rem'}}>
                  <p style={{marginBottom: '0.75rem', color: '#64748b'}}>Prefer to connect directly?</p>
                  <a 
                    href={`https://wa.me/917827551239?text=${encodeURIComponent('Hi, I have submitted my requirement on HSNL Advisory website. Please guide me with next steps.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                    style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}
                  >
                    <MessageCircle size={20} />
                    Connect on WhatsApp
                  </a>
                </div>
              </div>
            )}

            {showAssessmentForm && (
              <div className="assessment-form-container">
                <div className="trust-message-top">
                  <p>
                    Due to the bespoke nature of our services, we work with a limited number of clients. 
                    Submissions are reviewed based on relevance and transaction readiness.
                  </p>
                </div>

                <h3 className="assessment-title">Strategic Requirement – Assessment</h3>
                <p className="assessment-subtitle">Complete the form below for evaluation</p>
                
                {/* Google Form Embed */}
                <div className="google-form-embed">
                  <iframe 
                    src="https://docs.google.com/forms/d/e/1FAIpQLSfKaaOVD-0cpXkC6rQKUDcCe1PJT4TtH1bgiSVaZqkqcLS9qg/viewform?embedded=true" 
                    width="100%" 
                    height="1800" 
                    frameBorder="0" 
                    marginHeight="0" 
                    marginWidth="0"
                    title="Strategic Assessment Form"
                  >
                    Loading form...
                  </iframe>
                </div>

                <div className="trust-message-bottom">
                  <p>
                    Our team typically reviews high-priority mandates within 24 hours.
                  </p>
                </div>

                {/* Optional Full Screen Button */}
                <div className="form-fullscreen-option">
                  <a 
                    href="https://docs.google.com/forms/d/e/1FAIpQLSfKaaOVD-0cpXkC6rQKUDcCe1PJT4TtH1bgiSVaZqkqcLS9qg/viewform?usp=sf_link" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    Open Form in Full Screen
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
